import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  desktopNav,
  contactNav,
  mobileNav,
  type NavMenu,
  type NavLink,
  type NavCard,
} from '@/mocks/navigation';

const quickSearchKeywords = ['自由流停车', '数字红包', '九畴云市', '智慧农场', 'TOMA 智能体', '数字货架'];

function CardItem({ card, onNavigate }: { card: NavCard; onNavigate?: () => void }) {
  return (
    <Link
      to={card.href || '#'}
      onClick={onNavigate}
      className="group/card flex flex-col rounded-lg bg-background-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    >
      <div className="w-full h-24 overflow-hidden bg-background-200">
        {card.image ? (
          <img
            src={card.image}
            alt={card.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover/card:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className={`${card.icon} text-2xl text-primary-600 transition-colors duration-300 group-hover/card:text-primary-700`}></i>
          </div>
        )}
      </div>
      <div className="p-3 flex items-center min-h-[52px]">
        <span className="block text-sm font-medium text-foreground-900 leading-snug transition-colors duration-300 group-hover/card:text-primary-600">
          {card.name}
        </span>
      </div>
    </Link>
  );
}

function TextMenuPanel({ menu, onNavigate }: { menu: NavMenu; onNavigate: () => void }) {
  if (menu.columns) {
    return (
      <div className="flex flex-col gap-8 lg:gap-10 pr-10 lg:pr-12">
        {menu.columns.map((col, colIdx) => (
          <div key={col.title || `col-${colIdx}`}>
            {col.title ? (
              <div className="text-base font-bold text-foreground-950 mb-4">{col.title}</div>
            ) : null}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
              {col.links.map((link) =>
                link.href ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={onNavigate}
                    className="group/txt flex flex-col rounded-lg bg-background-100 px-4 py-3 cursor-pointer transition-colors duration-300 hover:bg-background-200"
                  >
                    <span className="block text-sm font-medium text-foreground-900 transition-colors duration-200 group-hover/txt:text-primary-600">
                      {link.label}
                    </span>
                  </Link>
                ) : (
                  <div key={link.label} className="flex items-center gap-2">
                    <span className="text-sm text-foreground-700">{link.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      待上线
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (menu.layout === 'horizontal') {
    const linkCount = menu.category!.links.length;
    const rowCols =
      linkCount >= 4 ? 'md:grid-cols-4' : linkCount === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
    return (
      <div className="pr-10 lg:pr-12">
        <div className="text-base font-bold text-foreground-950 mb-4">{menu.category!.title}</div>
        <div className={`grid grid-cols-1 ${rowCols} gap-3 md:gap-4`}>
          {menu.category!.links.map((link) => (
            <Link
              key={link.label}
              to={link.href || '#'}
              onClick={onNavigate}
              className="group/hz flex flex-col rounded-lg bg-background-100 px-4 py-4 transition-colors duration-300 hover:bg-background-200 cursor-pointer"
            >
              <span className="block text-sm font-medium text-foreground-900 transition-colors duration-200 group-hover/hz:text-primary-600">
                {link.label}
              </span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-8 md:gap-12 lg:gap-16 pr-10 lg:pr-12">
      <div className="md:w-56 shrink-0">
        <div className="text-base font-bold text-foreground-950 mb-4">{menu.category!.title}</div>
        <div className="flex flex-col gap-3">
          {menu.category!.links.map((link) => (
            <Link
              key={link.label}
              to={link.href || '#'}
              onClick={onNavigate}
              className="group/txt w-fit"
            >
              <span className="block text-sm text-foreground-800 transition-colors duration-200 group-hover/txt:text-primary-600">
                {link.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {menu.featured && (
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-foreground-950 mb-4">{menu.featured.title}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            {menu.featured.cards.map((card) => (
              <Link
                key={card.name}
                to={card.href || '#'}
                onClick={onNavigate}
                className="group/txt w-fit"
              >
                <span className="block text-sm text-foreground-800 transition-colors duration-200 group-hover/txt:text-primary-600">
                  {card.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MegaMenuPanel({
  menu,
  open,
  onEnter,
  onLeave,
  onClose,
  onRecordSource,
}: {
  menu: NavMenu;
  open: boolean;
  onEnter: () => void;
  onLeave: () => void;
  onClose: () => void;
  onRecordSource: (href: string | null) => void;
}) {
  const isCards = menu.variant === 'cards';
  const hasSwitcher = (menu.category?.links ?? []).some((link) => link.featured);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    setActiveIdx(null);
  }, [menu.label]);

  const activeFeatured =
    hasSwitcher && activeIdx !== null && menu.category?.links[activeIdx]?.featured
      ? menu.category!.links[activeIdx].featured!
      : menu.featured!;

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClickCapture={(event) => {
        const anchor = (event.target as HTMLElement).closest('a');
        if (anchor) onRecordSource(anchor.getAttribute('href'));
      }}
      className={`absolute top-full left-0 right-0 z-40 transition-all duration-[250ms] ease-out ${
        open
          ? 'opacity-100 visible translate-y-0'
          : 'opacity-0 invisible -translate-y-2 pointer-events-none'
      }`}
    >
      <div className="w-full bg-background-50 border-b border-background-200 shadow-[0_18px_44px_rgba(0,0,0,0.08)]">
        <div className="relative max-w-7xl mx-auto w-full px-4 md:px-6 py-8">
          {/* 关闭按钮 */}
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭菜单"
            className="absolute top-6 right-4 md:right-6 w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 cursor-pointer transition-colors duration-200 hover:text-foreground-950 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {isCards ? (
            <div className="flex gap-10">
              {/* 左栏：浅灰分类标题 + 垂直二级分类 */}
              <div className="w-56 shrink-0">
                <button
                  type="button"
                  onMouseEnter={() => setActiveIdx(null)}
                  className="block w-full text-left px-4 py-2.5 rounded-md bg-background-100 text-sm font-medium text-foreground-900 mb-3 transition-colors duration-300 hover:bg-background-200"
                >
                  {menu.category!.title}
                </button>
                <div className="flex flex-col gap-0.5">
                  {menu.category!.links.map((link, idx) =>
                    link.featured ? (
                      <button
                        key={link.label}
                        type="button"
                        onClick={() => setActiveIdx(idx)}
                        className={`group/link w-full text-left flex items-start gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors duration-300 ${
                          activeIdx === idx ? 'bg-background-100' : 'hover:bg-background-100'
                        }`}
                      >
                        <span className="flex-1">
                          <span
                            className={`block text-sm whitespace-nowrap transition-colors duration-300 ${
                              activeIdx === idx
                                ? 'text-primary-600 font-semibold'
                                : 'text-foreground-800 group-hover/link:text-primary-600'
                            }`}
                          >
                            {link.label}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href || '#'}
                        onClick={onClose}
                        className="group/link flex items-start gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors duration-300 hover:bg-background-100"
                      >
                        <span className="flex-1">
                          <span className="block text-sm text-foreground-800 whitespace-nowrap transition-colors duration-300 group-hover/link:text-primary-600">
                            {link.label}
                          </span>
                        </span>
                      </Link>
                    )
                  )}
                </div>
              </div>

              {/* 右栏：板块标题 + 多行产品卡片（仅「产品」菜单保留图片） */}
              <div className="flex-1 min-w-0 pr-10">
                <Link
                  to={activeFeatured.href || menu.href || '/products'}
                  onClick={onClose}
                  className="group/title flex w-fit items-center gap-1 text-base font-bold text-foreground-950 mb-4 cursor-pointer transition-colors duration-300 hover:text-primary-600"
                >
                  <span>{activeFeatured.title}</span>
                  <i className="ri-arrow-right-s-line text-lg transition-transform duration-300 group-hover/title:translate-x-0.5"></i>
                </Link>
                {activeFeatured.display === 'text' ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {activeFeatured.cards.map((card) => (
                      <Link
                        key={card.name}
                        to={card.href || '#'}
                        onClick={onClose}
                        className="group/aiot flex flex-col rounded-lg bg-background-100 px-4 py-4 cursor-pointer transition-colors duration-300 hover:bg-background-200"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium text-foreground-900 transition-colors duration-200 group-hover/aiot:text-primary-600">
                          {card.icon && (
                            <i className={`${card.icon} text-base text-primary-600`}></i>
                          )}
                          {card.name}
                        </span>
                        {card.desc && (
                          <span className="mt-1.5 text-xs text-foreground-600 leading-relaxed">
                            {card.desc}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4">
                    {activeFeatured.cards.map((card) => (
                      <CardItem key={card.name} card={card} onNavigate={onClose} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <TextMenuPanel menu={menu} onNavigate={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<NavMenu | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [hoverLabel, setHoverLabel] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // 滚动后导航使用实底样式，滚动前在深色主视觉上保持透明（全站统一）
  const solid = scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
    setActiveMenu(null);
    setPanelOpen(false);
    setPinned(false);
    setHoverLabel(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      window.setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleMenuEnter = (menu: NavMenu) => {
    clearCloseTimer();
    setHoverLabel(menu.label);
    setActiveMenu(menu);
    setPanelOpen(true);
  };

  const handleMenuLeave = () => {
    setHoverLabel(null);
    if (pinned) return;
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => {
      setPanelOpen(false);
      closeTimer.current = null;
    }, 200);
  };

  const handleMenuClick = (menu: NavMenu) => {
    clearCloseTimer();
    if (panelOpen && pinned && activeMenu?.label === menu.label) {
      setPanelOpen(false);
      setPinned(false);
      return;
    }
    setActiveMenu(menu);
    setPanelOpen(true);
    setPinned(true);
  };

  const closePanel = () => {
    clearCloseTimer();
    setPanelOpen(false);
    setPinned(false);
  };

  // 记录“从哪个菜单点进这个页面”，这样像 /solutions/smart-parking 这种
  // 本身属于「解决方案」、但也会从「客户案例」进入的页面，能亮用户实际点击的菜单。
  const recordSource = (href: string | null) => {
    if (!href || href === '#') return;
    const label = activeMenu?.label;
    if (!label) return;
    try {
      sessionStorage.setItem(`nav-source:${href}`, label);
    } catch {
      /* 忽略存储异常 */
    }
  };

  const openSearchPanel = () => {
    clearCloseTimer();
    setPanelOpen(false);
    setActiveMenu(null);
    setOpen(false);
    setOpenGroup(null);
    setSearchOpen((v) => !v);
  };

  const closeSearchPanel = () => setSearchOpen(false);

  // 同一路径可能同时出现在多个菜单里（例如 /solutions/smart-parking 既属于「解决方案」分类，
  // 又作为案例挂在「客户案例」下）。为避免多个菜单同时高亮，这里统一计算唯一的激活菜单：
  // 精确命中菜单首页 > 命中该菜单自身的分类链接 > 命中案例等关联链接，同级取导航顺序靠前者。
  const activeMenuLabel = (() => {
    let best: string | null = null;
    let bestScore = 0;
    desktopNav.forEach((item) => {
      if (!('category' in item) && !('columns' in item)) return;
      const menu = item as NavMenu;
      let score = 0;
      if (location.pathname === menu.href) {
        score = 3;
      } else if (menu.category?.links.some((i) => i.href === location.pathname)) {
        score = 2;
      } else if (menu.columns?.some((c) => c.links.some((i) => i.href === location.pathname))) {
        score = 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = menu.label;
      }
    });
    return best;
  })();

  // 记录从哪个菜单进入当前页面（刷新后依然保留），用于像 /solutions/smart-parking 这种
  // 同时挂在多个菜单下的页面，亮用户实际点击进入的菜单。
  const sourceMenuLabel = (() => {
    try {
      return sessionStorage.getItem(`nav-source:${location.pathname}`);
    } catch {
      return null;
    }
  })();

  // 高亮优先跟随鼠标悬停/点击的模块；没在操作时先回落到“点击来源菜单”，最后才回落到当前页面所属模块。
  const highlightedMenuLabel =
    hoverLabel ??
    (panelOpen ? activeMenu?.label ?? null : null) ??
    sourceMenuLabel ??
    activeMenuLabel;

  const isMenuActive = (menu: NavMenu) => menu.label === highlightedMenuLabel;
  const isLinkActive = (link: NavLink) => location.pathname === link.href;

  const menuButtonClass = (active: boolean, isOpen: boolean) =>
    `relative flex items-center gap-1 px-2 py-1 rounded-md whitespace-nowrap cursor-pointer transition-colors duration-[250ms] ${
      active || isOpen
        ? solid
          ? 'text-primary-600 font-semibold'
          : 'text-background-50 font-semibold'
        : solid
          ? 'text-foreground-800 group-hover:text-primary-600'
          : 'text-background-200 group-hover:text-primary-300'
    }`;

  // 纯链接入口（如「首页」）保持与下拉菜单（如「产品技术」）一致的字重，不做加粗高亮
  const linkClass = (active: boolean) =>
    `relative flex items-center gap-1 px-2 py-1 rounded-md whitespace-nowrap cursor-pointer transition-colors duration-[250ms] ${
      active
        ? solid
          ? 'text-primary-600'
          : 'text-background-50'
        : solid
          ? 'text-foreground-800 hover:text-primary-600'
          : 'text-background-200 hover:text-primary-300'
    }`;

  const closeMobile = () => {
    setOpen(false);
    setOpenGroup(null);
  };

  const runSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = search.trim();
    if (!keyword) return;
    closePanel();
    closeMobile();
    closeSearchPanel();
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="relative">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center h-16 lg:h-[72px]">
            {/* 品牌（仅品牌展示，不再跳转首页） — 左侧弹性区 */}
            <div className="flex-1 flex items-center">
              <div className="flex items-center gap-3 select-none shrink-0">
                <div className="w-11 h-11 lg:w-12 lg:h-12 overflow-hidden flex items-center justify-center">
                  <img
                    src="https://public.readdy.ai/ai/img_res/6c22b308-b8fb-4d44-8030-6a8e2c9b6142.png"
                    alt="华辉数智 logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div
                  className={`text-xl lg:text-2xl whitespace-nowrap leading-none transition-colors ${
                    solid ? 'text-foreground-950' : 'text-background-50'
                  }`}
                  style={{ fontFamily: "'Microsoft YaHei', '微软雅黑', sans-serif", fontWeight: 300 }}
                >
                  华辉数智
                </div>
              </div>
            </div>

            {/* 桌面导航 — 居中 */}
            <nav className="hidden md:flex items-center gap-0 lg:gap-1">
              {desktopNav.map((item) =>
                'category' in item || 'columns' in item ? (
                  <div
                    key={item.label}
                    className="relative group"
                    onMouseEnter={() => handleMenuEnter(item)}
                    onMouseLeave={handleMenuLeave}
                  >
                    <button
                      type="button"
                      onClick={() => handleMenuClick(item)}
                      className={`${menuButtonClass(
                        isMenuActive(item),
                        panelOpen && activeMenu?.label === item.label
                      )} text-xs lg:text-sm`}
                      aria-label={`${item.label}菜单`}
                    >
                      <span>{item.label}</span>
                      <i
                        className={`ri-arrow-down-s-line text-xs lg:text-sm transition-transform duration-200 ${
                          panelOpen && activeMenu?.label === item.label
                            ? 'rotate-180'
                            : 'group-hover:rotate-180'
                        }`}
                      ></i>
                      <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 h-[2px] w-6 bg-primary-500 origin-center scale-x-0 group-hover:scale-x-100 transition-transform duration-[250ms] ease-out pointer-events-none" />
                    </button>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href || '#'}
                    className={`${linkClass(isLinkActive(item))} text-xs lg:text-sm`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </nav>

            {/* 右侧弹性区 — 搜索图标 + 移动端汉堡 */}
            <div className="flex-1 flex items-center justify-end gap-1 md:gap-2">
              <button
                type="button"
                onClick={openSearchPanel}
                aria-label="打开搜索"
                aria-expanded={searchOpen}
                className={`w-10 h-10 flex items-center justify-center rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  solid
                    ? 'text-foreground-800 hover:text-primary-600 hover:bg-background-100'
                    : 'text-background-50 hover:text-primary-300'
                }`}
              >
                <i className="ri-search-line text-xl"></i>
              </button>
              <button
                className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
                onClick={() => setOpen((v) => !v)}
                aria-label="菜单"
              >
                <i
                  className={`text-2xl transition-colors ${open ? 'ri-close-line' : 'ri-menu-line'} ${
                    solid ? 'text-foreground-950' : 'text-background-50'
                  }`}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {/* 通栏 Mega Menu 面板 */}
        {activeMenu && (
          <MegaMenuPanel
            menu={activeMenu}
            open={panelOpen}
            onEnter={() => handleMenuEnter(activeMenu)}
            onLeave={handleMenuLeave}
            onClose={closePanel}
            onRecordSource={recordSource}
          />
        )}

        {/* 通栏搜索面板 */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 z-40">
            <div className="w-full bg-background-50 border-b border-background-200 shadow-[0_18px_44px_rgba(0,0,0,0.08)]">
              <div className="max-w-7xl mx-auto w-full px-4 md:px-6 py-6 md:py-8">
                <form onSubmit={runSearch} role="search" className="flex items-center gap-4">
                  <div className="flex-1 flex items-center gap-3 border-b border-background-300 pb-3 transition-colors focus-within:border-primary-500">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-search-line text-lg text-foreground-400"></i>
                    </div>
                    <input
                      ref={searchInputRef}
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="搜索"
                      aria-label="站内搜索"
                      className="flex-1 min-w-0 bg-transparent text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={closeSearchPanel}
                    aria-label="关闭搜索"
                    className="w-10 h-10 flex items-center justify-center rounded-full text-foreground-600 whitespace-nowrap cursor-pointer transition-colors hover:text-foreground-950 hover:bg-background-100 shrink-0"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </form>

                <div className="mt-5 md:mt-7">
                  <div className="text-xs font-medium tracking-wider text-foreground-400 mb-3">
                    快速查找
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickSearchKeywords.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          setSearch(tag);
                          closeSearchPanel();
                          navigate(`/search?q=${encodeURIComponent(tag)}`);
                        }}
                        className="px-3 py-1.5 rounded-full bg-background-100 text-xs md:text-sm text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 移动端全屏菜单 */}
      {open && (
        <div className="md:hidden bg-foreground-950/95 backdrop-blur-md border-b border-background-50/10 px-4 pb-6 max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <nav className="flex flex-col gap-1 mt-4">
            {mobileNav.map((item) => {
              if ('category' in item || 'columns' in item) {
                const menu = item as NavMenu;
                const expanded = openGroup === menu.label;
                return (
                  <div key={menu.label} className="border-b border-background-50/10">
                    <button
                      className="w-full flex items-center justify-between py-3 text-sm cursor-pointer text-background-50"
                      onClick={() => setOpenGroup(expanded ? null : menu.label)}
                    >
                      <span className={isMenuActive(menu) ? 'text-primary-400 font-semibold' : ''}>
                        {menu.label}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line transition-transform duration-200 ${
                          expanded ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {expanded && (
                      <div className="pb-3 pl-3 flex flex-col gap-1">
                        {menu.columns ? (
                          menu.columns.map((col, colIdx) => (
                            <div key={col.title || `col-${colIdx}`} className="py-1">
                              {col.title ? (
                                <div className="text-[11px] font-semibold tracking-widest text-background-300 py-1.5">
                                  {col.title}
                                </div>
                              ) : null}
                              {col.links.map((sub) =>
                                sub.href ? (
                                  <Link
                                    key={sub.label}
                                    to={sub.href}
                                    onClick={closeMobile}
                                    className="py-2 pl-2 text-sm text-background-200 cursor-pointer hover:text-background-50 block"
                                  >
                                    {sub.label}
                                  </Link>
                                ) : (
                                  <div
                                    key={sub.label}
                                    className="py-2 pl-2 text-sm text-background-300 flex items-center gap-2 opacity-70"
                                  >
                                    {sub.label}
                                    <span className="text-[10px]">待上线</span>
                                  </div>
                                )
                              )}
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="text-[11px] font-semibold tracking-widest text-background-300 py-1.5">
                              {menu.category?.title}
                            </div>
                            {menu.category?.links.map((sub) =>
                              sub.locked ? (
                                <div
                                  key={sub.label}
                                  className="py-2 pl-2 text-sm text-background-300 flex items-center gap-2 opacity-60"
                                >
                                  {sub.label}
                                  <i className="ri-lock-2-line text-xs"></i>
                                </div>
                              ) : (
                                <Link
                                  key={sub.label}
                                  to={sub.href || '#'}
                                  onClick={closeMobile}
                                  className="py-2 pl-2 text-sm text-background-200 cursor-pointer hover:text-background-50"
                                >
                                  {sub.label}
                                </Link>
                              )
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
              const link = item as NavLink;
              return (
                <Link
                  key={link.label}
                  to={link.href || '#'}
                  onClick={closeMobile}
                  className={`py-3 text-sm border-b border-background-50/10 cursor-pointer ${
                    location.pathname === link.href
                      ? 'text-primary-400 font-semibold'
                      : 'text-background-200'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to={contactNav.href || '#'}
              onClick={closeMobile}
              className="mt-4 text-center px-4 py-3 text-sm rounded-md bg-primary-500 text-background-50 cursor-pointer"
            >
              {contactNav.label}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}